import { FC } from 'react';
import classNames from 'classnames';
import styles from './heatmap.module.sass';

export interface HeatmapCell {
  /** Primary display value */
  value: string | number;
  /** Sub-label (e.g. percentage) */
  sub?: string;
  /** Heat intensity 0-1 (controls opacity) */
  intensity: number;
  /** Signal: 'neutral' (white), 'warn' (amber), 'breach' (red) */
  signal?: 'neutral' | 'warn' | 'breach';
}

export interface HeatmapRow {
  /** Row header label */
  label: string;
  /** Cells in this row */
  cells: HeatmapCell[];
}

export interface HeatmapProps {
  /** Column headers */
  columns: string[];
  /** Row data */
  rows: HeatmapRow[];
  /** Additional CSS class */
  className?: string;
  /** Give every data cell the same width and height, so the grid does not size
   *  its columns by their longest label. */
  uniformCells?: boolean;
  /** Receives the [row, column] index of the selected cell. Passing it is what
   *  makes the cells selectable. */
  onCellClick?: (rowIndex: number, colIndex: number) => void;
  /** The cell currently drawn as selected, as [rowIndex, columnIndex]. */
  selectedCell?: [number, number] | null;
}

/**
 * Heatmap — table with opacity-colored cells for cohort analysis.
 * Cells glow based on intensity × signal color.
 *
 * @example
 * <Heatmap
 *   columns={['Current', '1-30', '31-60', '61-90', '90+']}
 *   rows={[
 *     { label: '2025-Q1', cells: [
 *       { value: 312, sub: '78%', intensity: 0.78, signal: 'neutral' },
 *       { value: 48, sub: '12%', intensity: 0.12, signal: 'warn' },
 *     ]},
 *   ]}
 * />
 */
export const Heatmap: FC<HeatmapProps> = ({
  columns,
  rows,
  className,
  uniformCells = false,
  onCellClick,
  selectedCell = null,
}) => {
  const signalColor = (signal: string, intensity: number) => {
    const alpha = Math.max(0.03, intensity * 0.25);
    switch (signal) {
      case 'warn': return `rgba(var(--ds-hm-warn-rgb, 251,191,36), ${alpha})`;
      case 'breach': return `rgba(var(--ds-hm-breach-rgb, 248,113,113), ${alpha})`;
      default: return `rgba(var(--ds-hm-neutral-rgb, 255,255,255), ${alpha})`;
    }
  };

  return (
    <div className={classNames(styles.heatmapWrap, className)}>
      <table
        className={classNames(styles.heatmap, {
          [styles.uniform]: uniformCells,
          [styles.clickable]: Boolean(onCellClick),
        })}
      >
        <thead>
          <tr>
            <th className={styles.rowHeader} />
            {columns.map((col) => (
              <th key={col}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={row.label}>
              <th className={styles.rowHeader}>{row.label}</th>
              {row.cells.map((cell, idx) => {
                const isSelected =
                  selectedCell !== null && selectedCell[0] === rowIdx && selectedCell[1] === idx;
                return (
                  <td
                    key={idx}
                    className={classNames({ [styles.selected]: isSelected })}
                    style={{ background: signalColor(cell.signal || 'neutral', cell.intensity) }}
                    onClick={onCellClick ? () => onCellClick(rowIdx, idx) : undefined}
                    onKeyDown={
                      onCellClick
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onCellClick(rowIdx, idx);
                            }
                          }
                        : undefined
                    }
                    role={onCellClick ? 'button' : undefined}
                    tabIndex={onCellClick ? 0 : undefined}
                    aria-pressed={onCellClick ? isSelected : undefined}
                  >
                    {cell.value}
                    {cell.sub && <span className={styles.cellSub}>{cell.sub}</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

Heatmap.displayName = 'Heatmap';

export default Heatmap;
